import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { useEventos } from "../../context/EventosContext"; // alterado para CursosContext
import Sidebar from "../../components/sidebar/Sidebar";

import {
  PageLayout,
  Container,
  Header,
  Title,
  Subtitle,
  SearchInput,
  AddButton,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  ActionIcons,
  EventInfo,
  EventTitle,
  EventSubtitle,
  SearchWrapper,
  SortWrapper,
  HighlightRow,
  ModalContent,
  ModalOverlay,
  ModalImageHeader,
  ModalSection,
  ModalInfoItem,
  ModalInfoIcon,
  ModalInfoText,
  ModalTag,
  ModalRememberButton,
  ModalCloseButton
} from "./styles";

import {
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Star,
  Calendar,
  MapPin,
  Lock,
  Unlock,
  Users,
  X,
  CalendarCheck,
  Clock, 
} from "lucide-react";


/* 🔹 Helper imagem */
const getImagemUrl = (imagem) => {
  if (!imagem) return "https://placehold.co/600x200?text=Sem+Imagem";
  if (imagem.startsWith("http")) return imagem;

  const baseURL = api.defaults.baseURL;
  return `${baseURL}/${imagem.replace(/^\/?/, "")}`;
};


/* 🔹 Helper data (MongoDB) */
const formatarData = (data) => {
  if (!data) return "—";
  if (typeof data === "object" && data.$date) {
    return new Date(data.$date).toLocaleDateString("pt-BR");
  }
  return new Date(data).toLocaleDateString("pt-BR");
};

const Cursos = () => {
  const navigate = useNavigate();
  const { eventos: cursos = [], setEventos: setCursos } = useEventos() || {};

  const [selectedId, setSelectedId] = useState(null);
  const [sortType, setSortType] = useState("az");
  const [query, setQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [cursoSelecionado, setCursoSelecionado] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🔹 Buscar cursos
  useEffect(() => {
    const fetchCursos = async () => {
      try {
        const response = await api.get("/api/content/cursos");
        setCursos(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Erro ao buscar cursos:", error);
        setCursos([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCursos();
  }, [setCursos]);

  // 🔹 Deletar curso
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Deseja excluir este curso?")) return;

    try {
      await api.delete(`/api/content/cursos/${id}`);
      setCursos((prev) => prev.filter((c) => (c._id || c.id) !== id));
    } catch (error) {
      alert("Erro ao deletar curso.");
    }
  };

  // 🔹 Toggle destaque
  const toggleHighlight = async (id, e) => {
    e.stopPropagation();
    const cursoAtual = cursos.find((c) => (c._id || c.id) === id);
    if (!cursoAtual) return;

    const novoValor = !cursoAtual.destacado;
    setCursos((prev) =>
      prev.map((c) =>
        (c._id || c.id) === id ? { ...c, destacado: novoValor } : c
      )
    );

    try {
      await api.put(`/api/content/cursos/${id}`, { destacado: novoValor });
    } catch (error) {
      setCursos((prev) =>
        prev.map((c) =>
          (c._id || c.id) === id ? { ...c, destacado: cursoAtual.destacado } : c
        )
      );
    }
  };

  // 🔹 Filtro
  const filtered = cursos.filter(
    (c) =>
      c.titulo?.toLowerCase().includes(query.toLowerCase()) ||
      c.local?.toLowerCase().includes(query.toLowerCase())
  );

  // 🔹 Ordenação
  const sortCursos = (list) => {
    const copy = [...list];
    if (sortType === "az") copy.sort((a, b) => a.titulo.localeCompare(b.titulo));
    if (sortType === "za") copy.sort((a, b) => b.titulo.localeCompare(a.titulo));
    if (sortType === "highlighted")
      copy.sort(
        (a, b) =>
          (b.destacado ? 1 : 0) - (a.destacado ? 1 : 0) ||
          a.titulo.localeCompare(b.titulo)
      );
    return copy;
  };

  const displayed = sortCursos(filtered);

  return (
    <PageLayout>
      <Sidebar />

      <Container>
        <Header>
          <div>
            <Title>Gerenciar cursos</Title>
            <Subtitle>Crie e gerencie cursos da comunidade</Subtitle>
          </div>

          <AddButton onClick={() => navigate("/novo-curso")}>
            <Plus size={18} />
            Novo curso
          </AddButton>
        </Header>

        <SortWrapper>
          <label>Ordenar por:</label>
          <select value={sortType} onChange={(e) => setSortType(e.target.value)}>
            <option value="az">A–Z</option>
            <option value="za">Z–A</option>
            <option value="highlighted">Destaques</option>
          </select>
        </SortWrapper>

        <SearchWrapper>
          <Search size={18} />
          <SearchInput
            placeholder="Pesquisar cursos..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </SearchWrapper>

        <Table>
          <thead>
            <TableRow header>
              <TableHeader>Título</TableHeader>
              <TableHeader>Data início</TableHeader>
              <TableHeader>Local</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Ações</TableHeader>
            </TableRow>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" style={{ padding: 40, textAlign: "center" }}>
                  Carregando cursos...
                </td>
              </tr>
            ) : displayed.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ padding: 40, textAlign: "center" }}>
                  Nenhum curso encontrado.
                </td>
              </tr>
            ) : (
              displayed.map((curso) => {
                const cursoId = curso._id || curso.id;
                return (
                  <HighlightRow
                    key={cursoId}
                    highlighted={curso.destacado}
                    selected={selectedId === cursoId}
                    onClick={() => setSelectedId(cursoId)}
                  >
                    <TableCell>
                      <EventInfo>
                        {curso.destacado && <span>⭐ DESTACADO</span>}
                        <EventTitle>{curso.titulo}</EventTitle>
                        <EventSubtitle>{curso.descricao}</EventSubtitle>
                      </EventInfo>
                    </TableCell>

                    <TableCell>{formatarData(curso.dataInicio)}</TableCell>
                    <TableCell>{curso.local}</TableCell>
                    <TableCell>
                      <StatusBadge>{curso.status}</StatusBadge>
                    </TableCell>

                    <TableCell>
                      <ActionIcons>
                        <Star
                          size={18}
                          color={curso.destacado ? "#2563eb" : "#9ca3af"}
                          onClick={(e) => toggleHighlight(cursoId, e)}
                        />
                        <Eye
                          size={18}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCursoSelecionado(curso);
                            setShowModal(true);
                          }}
                        />
                        <Edit
                          size={18}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/editar-curso/${cursoId}`);
                          }}
                        />
                        <Trash2
                          size={18}
                          color="#EF4444"
                          onClick={(e) => handleDelete(cursoId, e)}
                        />
                      </ActionIcons>
                    </TableCell>
                  </HighlightRow>
                );
              })
            )}
          </tbody>
        </Table>
      </Container>

      {/* 🔥 MODAL VISUALIZAÇÃO */}
{showModal && cursoSelecionado && (
  <ModalOverlay onClick={() => setShowModal(false)}>
    <ModalContent onClick={(e) => e.stopPropagation()}>
      <ModalCloseButton onClick={() => setShowModal(false)}>
        <X size={18} />
      </ModalCloseButton>

      {/* IMAGEM */}
      <ModalImageHeader>
        {cursoSelecionado.imagem ? (
          <img
            src={getImagemUrl(cursoSelecionado.imagem)}
            alt={cursoSelecionado.titulo}
          />
        ) : (
          <div className="placeholder">Sem imagem</div>
        )}
      </ModalImageHeader>

      {/* CONTEÚDO */}
      <div style={{ padding: "1.5rem", overflowY: "auto" }}>
        <h2>{cursoSelecionado.titulo}</h2>
        <p>{cursoSelecionado.descricao}</p>

        {/* INFO PRINCIPAL */}
        <ModalSection>
          <ModalInfoItem>
            <ModalInfoIcon color="#2563eb">
              <Calendar size={18} />
            </ModalInfoIcon>
            <ModalInfoText>
              Início: {formatarData(cursoSelecionado.dataInicio)}
            </ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#16a34a">
              <CalendarCheck size={18} />
            </ModalInfoIcon>
            <ModalInfoText>
              Fim: {formatarData(cursoSelecionado.dataFim)}
            </ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#f59e0b">
              <Clock size={18} />
            </ModalInfoIcon>
            <ModalInfoText>
              Horário: {cursoSelecionado.hora || "Não informado"}
            </ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#0ea5e9">
              <Users size={18} />
            </ModalInfoIcon>
            <ModalInfoText>
              {cursoSelecionado.vagas ? `${cursoSelecionado.vagas} vagas` : "Vagas ilimitadas"}
            </ModalInfoText>
          </ModalInfoItem>

          <ModalInfoItem>
            <ModalInfoIcon color="#16a34a">
              <MapPin size={18} />
            </ModalInfoIcon>
            <ModalInfoText>{cursoSelecionado.local || "Não informado"}</ModalInfoText>
          </ModalInfoItem>
        </ModalSection>

        {/* STATUS */}
        <ModalSection>
          <strong>Status</strong>
          <StatusBadge>{cursoSelecionado.status}</StatusBadge>
        </ModalSection>

        {/* TAGS */}
        <ModalSection>
          <strong>Tags</strong>
          {cursoSelecionado.tags?.length > 0 ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              {cursoSelecionado.tags.map((tag, i) => (
                <ModalTag key={i}>#{tag}</ModalTag>
              ))}
            </div>
          ) : (
            <p>Sem tags</p>
          )}
        </ModalSection>

        {/* CONTEÚDOS */}
        <ModalSection>
          <strong>Conteúdos</strong>
          {cursoSelecionado.conteudos?.length > 0 ? (
            cursoSelecionado.conteudos.map((conteudo, idx) => (
              <div key={idx} style={{ marginTop: 8 }}>
                <p><strong>{conteudo.titulo}</strong></p>
                <ul>
                  {conteudo.itens
                    .filter((item) => item.trim() !== "")
                    .map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                </ul>
              </div>
            ))
          ) : (
            <p>Nenhum conteúdo cadastrado.</p>
          )}
        </ModalSection>

        {/* CONTATOS */}
        <ModalSection>
          <strong>Contatos</strong>
          {cursoSelecionado.contatos?.some(c => c.telefone || c.email || c.redeSocial) ? (
            cursoSelecionado.contatos.map((c, i) => (
              <div key={i}>
                {c.telefone && <p>📞 {c.telefone}</p>}
                {c.email && <p>📧 {c.email}</p>}
                {c.redeSocial && <p>🌐 {c.redeSocial}</p>}
              </div>
            ))
          ) : (
            <p>Nenhum contato informado.</p>
          )}
        </ModalSection>
      </div>
    </ModalContent>
  </ModalOverlay>
)}


    </PageLayout>
  );
};

export default Cursos;
